'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import { NavBack } from '@susu/headless-commerce/features/navigation/components/NavBack'
import { NavCloseButton } from '@susu/headless-commerce/features/navigation/components/NavCloseButton'
import { ClientNavCountrySelector } from '@susu/headless-commerce/features/navigation/components/NavCountrySelector/ClientNavCountrySelector'
import navSubmenuStyles from '@susu/headless-commerce/features/navigation/components/NavSubmenu/NavSubmenu.module.scss'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackCountrySelectorClicked } from '@susu/headless-commerce/utils/tracking/trackCountrySelectorClicked'
import { useI18N } from '@susu/i18n/client'
import classNames from 'classnames'
import { useCallback } from 'react'
import styles from './NavLocaleCountry.module.scss'

export type ClientNavLocaleCountryProps = {
  className?: string
}

export const ClientNavLocaleCountry = ({
  className,
}: ClientNavLocaleCountryProps) => {
  useSignals()

  const country = useCountry()
  const locale = useLocale()
  const { url, locationId } = URLData()
  const pageType = usePageType()
  const { t } = useI18N()
  const { openCountrySelector, isCountrySelectorOpened } = useClientNav()

  const handleButtonClick = useCallback(() => {
    openCountrySelector()

    trackCountrySelectorClicked({
      country,
      eventLocation: 'menu',
      locale,
      locationId,
      pageType,
      url,
    })
  }, [openCountrySelector, country, locale, locationId, pageType, url])

  const buttonClass = classNames(
    'nav__locale-btn',
    'nav__locale-btn--country',
    styles['nav__locale-btn--country'],
    className,
  )

  const countrySelectorClass = classNames(
    styles['nav__country-selector'],
    'nav__country-selector',
    navSubmenuStyles['nav__submenu'],
    'nav__submenu',
    {
      [`${navSubmenuStyles['nav__submenu--opened']} nav__submenu--opened`]:
        isCountrySelectorOpened.value,
    },
    className,
  )

  return (
    <>
      <button
        className={buttonClass}
        type="button"
        onClick={handleButtonClick}
        data-testid="nav-locale-country"
      >
        {country.name}
      </button>
      <div className={countrySelectorClass}>
        <NavBack />
        <NavCloseButton className={'nav__submenu-close-btn'} />
        <span className={`${styles['nav__country-title']} nav__submenu-title`}>
          {t('navigation.country')}
        </span>
        <ClientNavCountrySelector />
      </div>
    </>
  )
}
